// src/pages/KnowledgeBase.jsx
// The Library: guides and training, with a search box. Super Admins add and
// edit articles with the same rich-text box as Email Templates (or raw HTML).
// 3.35.0: each person sees only the articles for them (the server decides:
// level, feature in Roles & Access, client type), in sections; their
// training (Mark as done, Preferences → Show my training); Super Admins also
// get Drafts, Archived, Without a video, video upload to our own storage and
// the video prompt for each article.
import { useState, useEffect, useMemo, useRef } from 'react';
import { useAuth } from '../hooks/useAuth';
import { usePreferences } from '../hooks/usePreferences';
import { PILL, PILL_ON, PILL_ROW } from '../theme/tabs';
import { useNotify } from '../components/Notifications';
import { RichText, EDITOR_CSS } from '../components/EmailEditorParts';
import { API_BASE } from '../firebase/config';
import { timeZone } from '../utils/timeZone';
import Loader from '../components/Loader';
import Emo, { fluxHtml, emoLabel } from '../components/Emo';

const EMPTY_FORM = { title: '', icon: '', content: '', videoUrl: '', order: 99, section: 'start', audience: 'all', needs: '', clientType: 'all',
  status: 'draft', training: true, videoScript: '', videoPrompt: '' };
const COLORS = ['var(--c-e3f2fd)', 'var(--c-e8f5e9)', 'var(--c-fce4ec)', 'var(--c-fff8e1)', 'var(--c-f3e5f5)'];
const ICON_COLORS = ['var(--t-1565c0)', 'var(--t-2e7d32)', 'var(--t-b71c1c)', 'var(--t-e65100)', 'var(--t-6a1b9a)'];

// Article HTML → plain text for search and summaries. DOMParser doesn't
// run scripts or load images; a space before block ends keeps words apart.
function stripHtml(html) {
  const spaced = (html || '').replace(/<\/(p|div|li|h[1-6]|td|th|tr|blockquote)>|<br\s*\/?>/gi, m => ` ${m}`);
  const doc = new DOMParser().parseFromString(spaced, 'text/html');
  return (doc.body.textContent || '').replace(/\s+/g, ' ').trim();
}

const escapeRe = s => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

// Summary for a card: the start of the article, or the bit around the
// first match while searching
function summary(text, words) {
  let start = 0;
  if (words.length) {
    const lower = text.toLowerCase();
    const hits = words.map(w => lower.indexOf(w)).filter(i => i >= 0);
    const first = hits.length ? Math.min(...hits) : 0;
    if (first > 60) start = Math.max(0, text.lastIndexOf(' ', first - 40) + 1);
  }
  const cut = text.slice(start, start + 140);
  return (start > 0 ? '…' : '') + cut + (start + 140 < text.length ? '…' : '');
}

// Marks the search words inside a piece of text
function Highlight({ text, re }) {
  if (!re) return text;
  return text.split(re).map((part, i) => (i % 2 ? <mark key={i} style={S.mark}>{part}</mark> : part));
}

// Who an article is for, as Super Admins see it on the cards (3.35.0)
const AUDIENCE_LABEL = { all: 'Everyone', managers: 'Managers and up', admins: 'Admins and up', master_admins: 'Master Admins and up', super_admins: 'Super Admins' };
const CLIENT_LABEL = { all: 'All clients', business: 'Business clients only', individual: 'Individual clients only' };
const STATUS_TAG = {
  draft: { label: 'Draft', background: 'var(--c-fff8e1)', color: 'var(--t-e65100)' },
  published: { label: 'Published', background: 'var(--c-e8f5e9)', color: 'var(--t-2e7d32)' },
  archived: { label: 'Archived', background: 'var(--c-eceff1)', color: 'var(--t-546e7a)' },
};

// A YouTube or Vimeo link → its player address
function embedUrl(url) {
  if (!url) return null;
  const yt = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([\w-]+)/);
  if (yt) return `https://www.youtube.com/embed/${yt[1]}`;
  const vimeo = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  if (vimeo) return `https://player.vimeo.com/video/${vimeo[1]}`;
  return url;
}
const hasVideo = a => !!(a.videoUrl || a.hasVideoFile);
const mb = n => `${(n / 1048576).toFixed(n > 104857600 ? 0 : 1)} MB`;

export default function KnowledgeBase() {
  const { getToken, viewAs } = useAuth();
  const { notify, confirm }       = useNotify();
  const { prefs } = usePreferences();
  const [articles, setArticles]   = useState([]);
  const [sections, setSections]   = useState([]);
  const [features, setFeatures]   = useState([]);
  const [done, setDone]           = useState({});
  const [isAdmin, setIsAdmin]     = useState(false); // Super Admin, not using View as (the server decides)
  const [loading, setLoading]     = useState(true);
  const [view, setView]           = useState('articles'); // articles | training | drafts | archived | novideo
  const [selected, setSelected]   = useState(null); // article id currently open
  const [editing, setEditing]     = useState(null); // article id or 'new'
  const [form, setForm]           = useState(EMPTY_FORM);
  const [htmlMode, setHtmlMode]   = useState(false); // editing the raw HTML
  const [editorKey, setEditorKey] = useState(0);     // remounts the rich-text box
  const [saving, setSaving]       = useState(false);
  const [error, setError]         = useState('');
  const [query, setQuery]         = useState('');
  const [prompt, setPrompt]       = useState(null);  // the article whose video prompt is shown
  const [upload, setUpload]       = useState(null);  // { name, pct } while a video uploads
  const [playUrl, setPlayUrl]     = useState(null);  // { id, url } for a video in our storage

  const api = async (path, opts = {}) => {
    const token = await getToken();
    const res = await fetch(`${API_BASE}${path}`, { ...opts, headers: { Authorization: `Bearer ${token}`, ...(opts.body ? { 'Content-Type': 'application/json' } : {}), ...(opts.headers || {}) } });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(json.error || 'Something went wrong.');
    return json;
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadArticles(); }, [viewAs]);

  // Opened from ❓ Help (3.35.0): straight to that article
  useEffect(() => {
    const open = id => { if (id) { setEditing(null); setSelected(id); } };
    try { open(sessionStorage.getItem('kb.open')); sessionStorage.removeItem('kb.open'); } catch { /* ignore */ }
    const onOpen = e => { open(e.detail); try { sessionStorage.removeItem('kb.open'); } catch { /* ignore */ } };
    window.addEventListener('kb-open', onOpen);
    // 3.39.1: clicking Library in the menu while here goes back to its front page
    const onReselect = e => { if (e.detail === 'kb') { setEditing(null); setSelected(null); window.scrollTo({ top: 0 }); } };
    window.addEventListener('tab-reselect', onReselect);
    return () => { window.removeEventListener('kb-open', onOpen); window.removeEventListener('tab-reselect', onReselect); };
  }, []);

  async function loadArticles() {
    try {
      const j = await api('/portal/kb');
      setArticles(j.data || []);
      setSections(j.sections || []);
      setFeatures(j.features || []);
      setDone(j.done || {});
      setIsAdmin(!!j.editor && !viewAs);
    } catch (e) {
      setError("Couldn't load the Library.");
    }
    setLoading(false);
  }

  const showTraining = prefs.showTraining !== false;
  const published = articles.filter(a => a.status === 'published');
  const lists = {
    articles: published,
    training: published.filter(a => a.training),
    drafts: articles.filter(a => a.status === 'draft'),
    archived: articles.filter(a => a.status === 'archived'),
    novideo: articles.filter(a => a.status !== 'archived' && !hasVideo(a)),
  };
  const views = [
    { id: 'articles', label: '📖 Articles' },
    ...(showTraining && lists.training.length ? [{ id: 'training', label: '🎓 My training' }] : []),
    ...(isAdmin ? [
      { id: 'drafts', label: `📝 Drafts (${lists.drafts.length})` },
      { id: 'novideo', label: `🎬 Without a video (${lists.novideo.length})` },
      { id: 'archived', label: `🗄️ Archived (${lists.archived.length})` },
    ] : []),
  ];
  const current = views.some(v => v.id === view) ? view : 'articles';
  const trainingDone = lists.training.filter(a => done[a.id]).length;

  // Plain text of each article, worked out once per load
  const indexed = useMemo(() => articles.map(a => {
    const text = stripHtml(a.content);
    return { article: a, text, hay: `${a.title || ''} ${text}`.toLowerCase() };
  }), [articles]);

  // Every word typed must appear in the title or the text
  const words = useMemo(() => query.toLowerCase().split(/\s+/).filter(Boolean), [query]);
  const matchRe = useMemo(() => (words.length
    ? new RegExp(`(${[...words].sort((a, b) => b.length - a.length).map(escapeRe).join('|')})`, 'gi')
    : null), [words]);
  const inView = new Set(lists[current].map(a => a.id));
  const shown = indexed.filter(x => inView.has(x.article.id) && (!words.length || words.every(w => x.hay.includes(w))));

  // In sections, in the Library's order
  const grouped = sections.map(sec => ({ ...sec, items: shown.filter(x => x.article.section === sec.id) })).filter(g => g.items.length);

  function openEditor(id, values) {
    setForm(values);
    setHtmlMode(false);
    setEditorKey(k => k + 1);
    setError('');
    setEditing(id);
  }

  const valuesOf = a => ({
    title: a.title || '', icon: a.icon || '', content: a.content || '', videoUrl: a.videoUrl || '', order: a.order ?? 99,
    section: a.section || 'other', audience: a.audience || 'all', needs: a.needs || '', clientType: a.clientType || 'all',
    status: a.status || 'draft', training: a.training !== false, videoScript: a.videoScript || '', videoPrompt: a.videoPrompt || '',
  });

  function startEdit(article, e) {
    e?.stopPropagation();
    openEditor(article.id, valuesOf(article));
  }

  function startNew() {
    openEditor('new', { ...EMPTY_FORM, order: articles.length + 1 });
    setSelected(null);
  }

  // Rich text ⇄ HTML; going back to rich text loads what's in the HTML box
  function toggleHtml() {
    if (htmlMode) setEditorKey(k => k + 1);
    setHtmlMode(m => !m);
  }

  const bodyOf = v => {
    const order = parseInt(v.order, 10);
    return JSON.stringify({ ...v, title: v.title.trim(), videoUrl: v.videoUrl.trim(), needs: v.needs || null, order: Number.isFinite(order) ? order : 99 });
  };

  async function saveArticle() {
    if (!form.title.trim()) { setError('Please give the article a title.'); return; }
    setSaving(true);
    setError('');
    const isNew = editing === 'new';
    try {
      const j = await api(isNew ? '/portal/kb' : `/portal/kb/${editing}`, { method: isNew ? 'POST' : 'PUT', body: bodyOf(form) });
      await loadArticles();
      notify({ tone: 'success', title: isNew ? 'Article added' : 'Article saved', message: isNew ? 'You can add a video to it now.' : undefined });
      // A new article stays open in the editor, so a video can be uploaded to it
      if (isNew && j.id) setEditing(j.id); else setEditing(null);
    } catch (e) {
      setError(e.message || 'Failed to save article.');
    }
    setSaving(false);
  }

  // Publish, archive, restore: the article as it is, with a new status
  async function setStatus(article, status) {
    const words2 = { published: ['Publish this article?', 'Everyone it’s meant for will see it in the Library.', 'Publish'],
      archived: ['Move this article to the archive?', 'Only Super Admins will see it, under Archived. You can bring it back.', 'Archive'],
      draft: ['Move this article back to drafts?', 'Only Super Admins will see it until it’s published.', 'Move to drafts'] }[status];
    if (!(await confirm({ title: words2[0], message: words2[1], confirmLabel: words2[2] }))) return;
    try {
      await api(`/portal/kb/${article.id}`, { method: 'PUT', body: bodyOf({ ...valuesOf(article), status }) });
      await loadArticles();
      notify({ tone: 'success', title: { published: 'Published', archived: 'Moved to the archive', draft: 'Moved to drafts' }[status] });
    } catch (e) { setError(e.message); }
  }

  async function deleteArticle(id, e) {
    e?.stopPropagation();
    const ok = await confirm({
      title: 'Delete this article?',
      message: "It will be removed for everyone. This can't be undone. (Archive keeps it instead.) Its uploaded video stays in storage.",
      confirmLabel: 'Delete',
      danger: true,
    });
    if (!ok) return;
    try {
      await api(`/portal/kb/${id}`, { method: 'DELETE' });
      if (selected === id) setSelected(null);
      await loadArticles();
      notify({ tone: 'success', title: 'Article deleted' });
    } catch (e) {
      setError('Failed to delete article.');
    }
  }

  // Training: Mark as done / not done
  async function toggleDone(article) {
    const next = !done[article.id];
    setDone(d => { const x = { ...d }; if (next) x[article.id] = new Date().toISOString(); else delete x[article.id]; return x; });
    try { await api(`/portal/kb/${article.id}/done`, { method: 'POST', body: JSON.stringify({ done: next }) }); }
    catch (e) { setError(e.message); loadArticles(); }
  }

  // Watching the video counts as done (3.36.3): once at least 90% of it has
  // really played (skipping to the end doesn't count); Mark as done stays
  const watchedRef = useRef(null);
  function watched(article, v) {
    if (!showTraining || !article.training || article.status !== 'published' || viewAs || done[article.id] || watchedRef.current === article.id) return;
    if (!v.duration || !Number.isFinite(v.duration)) return;
    let seen = 0;
    for (let k = 0; k < v.played.length; k++) seen += v.played.end(k) - v.played.start(k);
    if (seen / v.duration < 0.9) return;
    watchedRef.current = article.id;
    toggleDone(article);
    notify({ tone: 'success', title: 'Marked as done', detail: 'You watched the whole video.' });
  }

  // Upload a video to our own storage: a link from the server, the file
  // straight to storage (with progress), then attach it to the article
  async function uploadVideo(id, file) {
    if (!file) return;
    setError('');
    try {
      const j = await api(`/portal/kb/${id}/video/upload-url`, { method: 'POST', body: JSON.stringify({ name: file.name, type: file.type, size: file.size }) });
      setUpload({ name: file.name, pct: 0 });
      await new Promise((resolve, reject) => {
        const x = new XMLHttpRequest();
        x.open('PUT', j.url);
        x.setRequestHeader('Content-Type', file.type);
        x.upload.onprogress = ev => ev.lengthComputable && setUpload({ name: file.name, pct: Math.round((ev.loaded / ev.total) * 100) });
        x.onload = () => (x.status < 300 ? resolve() : reject(new Error(`The upload failed (${x.status}).`)));
        x.onerror = () => reject(new Error('The upload failed. Check the connection and try again.'));
        x.send(file);
      });
      await api(`/portal/kb/${id}/video`, { method: 'POST', body: JSON.stringify({ path: j.path, name: file.name }) });
      setPlayUrl(null);
      await loadArticles();
      notify({ tone: 'success', title: 'Video uploaded', message: file.name });
    } catch (e) { setError(e.message); }
    setUpload(null);
  }

  const openArticle = articles.find(a => a.id === selected);
  const viewing = selected && openArticle && !editing;
  const editingArticle = editing && editing !== 'new' ? articles.find(a => a.id === editing) : null;

  // A video kept in our storage plays through a link that lasts 2 hours
  useEffect(() => {
    if (!viewing || !openArticle?.hasVideoFile || playUrl?.id === openArticle.id) return;
    let live = true;
    api(`/portal/kb/${openArticle.id}/video`).then(j => live && setPlayUrl({ id: openArticle.id, url: j.url })).catch(() => {});
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewing, openArticle?.id, openArticle?.hasVideoFile]);

  if (loading) return (
    <Loader label="Loading the Library…" />
  );

  const featureLabel = key => features.find(f => f.key === key)?.label || key;
  const tagsOf = a => isAdmin && (
    <>
      {a.status !== 'published' && <span style={{ ...S.tag, background: STATUS_TAG[a.status].background, color: STATUS_TAG[a.status].color }}>{STATUS_TAG[a.status].label}</span>}
      <span style={S.tag}>{AUDIENCE_LABEL[a.audience]}</span>
      {a.clientType !== 'all' && <span style={S.tag}>{CLIENT_LABEL[a.clientType]}</span>}
      {a.needs && <span style={S.tag} title={featureLabel(a.needs)}>Needs: {featureLabel(a.needs).replace(/^See the /, '').slice(0, 40)}</span>}
      {!a.training && <span style={S.tag}>Not in training</span>}
    </>
  );

  return (
    <div>
      <style>{EDITOR_CSS + KB_CSS}</style>

      {/* Header */}
      <div style={S.header}>
        <div style={{ minWidth: 0 }}>
          {viewing ? (
            <button onClick={() => setSelected(null)} style={S.backBtn}>
              ← Back to Library
            </button>
          ) : (
            <>
              <h2 style={S.pageTitle} className="cp-title"><Emo e="📚" /> Library</h2>
              <p style={S.pageSubtitle}>How to use the Fax Portal: guides and training for your role.</p>
            </>
          )}
        </div>
        {isAdmin && !selected && !editing && (
          <button onClick={startNew} style={S.addBtn}>+ Add Article</button>
        )}
        {isAdmin && viewing && (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button onClick={() => setPrompt(openArticle)} style={S.editBtn}><Emo e="🎬" /> Video prompt</button>
            <button onClick={(e) => startEdit(openArticle, e)} style={S.editBtn}><Emo e="✏️" /> Edit</button>
            {openArticle.status === 'draft' && <button onClick={() => setStatus(openArticle, 'published')} style={S.publishBtn}>✓ Publish</button>}
            {openArticle.status === 'archived'
              ? <button onClick={() => setStatus(openArticle, 'draft')} style={S.editBtn}>↩ Restore to drafts</button>
              : <button onClick={() => setStatus(openArticle, 'archived')} style={S.editBtn}><Emo e="🗄️" /> Archive</button>}
            <button onClick={(e) => deleteArticle(selected, e)} style={S.deleteBtn}><Emo e="🗑️" /> Delete</button>
          </div>
        )}
      </div>

      {error && <div role="alert" style={S.errorBanner}>{error}</div>}

      {/* Edit / New Form */}
      {editing && (
        <div style={S.formCard}>
          <h3 style={S.formTitle}>{editing === 'new' ? 'New article' : 'Edit article'}</h3>

          <div style={S.twoCol}>
            <div style={{ flex: '0 0 90px' }}>
              <label style={S.label} htmlFor="kb-icon">Icon</label>
              <input id="kb-icon" value={form.icon} onChange={e => setForm({ ...form, icon: e.target.value })} maxLength={8}
                style={{ ...S.input, textAlign: 'center', fontSize: 18 }} />
            </div>
            <div style={{ flex: '1 1 300px', minWidth: 0 }}>
              <label style={S.label} htmlFor="kb-title">Title</label>
              <input id="kb-title" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })}
                style={S.input} placeholder="e.g. How to send a fax" />
            </div>
          </div>
          <p style={S.hint}>The icon is an emoji (on a Mac: Control + Command + Space). Empty: the section's icon.</p>

          {/* Where it goes and who sees it */}
          <div style={S.twoCol}>
            <div style={S.field}>
              <label style={S.label} htmlFor="kb-section">Section</label>
              <select id="kb-section" value={form.section} onChange={e => setForm({ ...form, section: e.target.value })} style={S.input}>
                {sections.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
              </select>
            </div>
            <div style={S.field}>
              <label style={S.label} htmlFor="kb-audience">Who sees it</label>
              <select id="kb-audience" value={form.audience} onChange={e => setForm({ ...form, audience: e.target.value })} style={S.input}>
                {Object.entries(AUDIENCE_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              </select>
            </div>
            <div style={S.field}>
              <label style={S.label} htmlFor="kb-client">Clients</label>
              <select id="kb-client" value={form.clientType} onChange={e => setForm({ ...form, clientType: e.target.value })} style={S.input}>
                {Object.entries(CLIENT_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              </select>
            </div>
          </div>
          <label style={S.label} htmlFor="kb-needs">Only for people who can <span style={S.labelHint}>(a feature in Roles & Access; optional)</span></label>
          <select id="kb-needs" value={form.needs} onChange={e => setForm({ ...form, needs: e.target.value })} style={S.input}>
            <option value="">Anyone in “Who sees it”</option>
            {[...new Set(features.map(f => f.section))].map(sec => (
              <optgroup key={sec} label={sec}>
                {features.filter(f => f.section === sec).map(f => <option key={f.key} value={f.key}>{f.label}</option>)}
              </optgroup>
            ))}
          </select>
          <p style={S.hint}>The article shows only to people at that level and above whose role has this feature, so it follows each client’s Roles & Access.</p>

          <div style={S.labelRow}>
            <span style={{ ...S.label, margin: 0 }} id="kb-content-label">Article</span>
            <button type="button" onClick={toggleHtml} style={S.linkBtn}>
              {htmlMode ? 'Back to the editor' : 'Edit HTML'}
            </button>
          </div>
          {htmlMode ? (
            <textarea value={form.content} onChange={e => setForm(f => ({ ...f, content: e.target.value }))}
              aria-labelledby="kb-content-label" style={S.htmlBox} rows={16} spellCheck={false} />
          ) : (
            <RichText key={editorKey} labelId="kb-content-label" initial={form.content} phMap={{}} onFocusField={() => {}}
              placeholders={false} headings minHeight={260} onChange={v => setForm(f => ({ ...f, content: v }))} />
          )}
          <p style={S.hint}>
            {htmlMode ? 'Changes here show in the editor when you go back to it.' : 'Select text, then use the buttons above to format it.'}
          </p>

          {/* Video: uploaded to our storage, or a YouTube / Vimeo link */}
          <div style={S.videoBox}>
            <strong style={{ fontSize: 13, color: 'var(--t-00695c)' }}><Emo e="🎬" /> Video</strong>
            {editing === 'new' ? (
              <p style={S.hint}>Add the article first, then upload its video here.</p>
            ) : (
              <div style={{ marginTop: 6 }}>
                {editingArticle?.videoFile?.path && (
                  <div style={S.fileRow}>
                    <span>▶ {editingArticle.videoFile.name || 'Video'} · {mb(editingArticle.videoFile.size || 0)}</span>
                    <button type="button" style={S.linkBtn} onClick={async () => {
                      if (!(await confirm({ title: 'Remove this video from the article?', message: 'It no longer shows here. The file itself stays in storage (training videos are never deleted by the portal).', confirmLabel: 'Remove' }))) return;
                      try { await api(`/portal/kb/${editing}`, { method: 'PUT', body: bodyOf({ ...form, removeVideoFile: true }) }); await loadArticles(); } catch (e) { setError(e.message); }
                    }}>Remove</button>
                  </div>
                )}
                {upload ? (
                  <div style={S.hint} role="status">Uploading {upload.name}… {upload.pct}%
                    <div style={S.progressTrack}><div style={{ ...S.progressBar, width: `${upload.pct}%` }} /></div>
                  </div>
                ) : (
                  <label style={S.uploadBtn}>
                    {editingArticle?.videoFile?.path ? 'Replace the video' : <><Emo e="⬆" /> Upload a video</>}
                    <input type="file" accept="video/mp4,video/webm,video/quicktime" style={{ display: 'none' }}
                      onChange={e => { uploadVideo(editing, e.target.files?.[0]); e.target.value = ''; }} />
                  </label>
                )}
                <p style={S.hint}>MP4, WebM or MOV, up to 500 MB. Kept in our own storage (never deleted automatically); only people who can read the article can play it.</p>
              </div>
            )}
            <label style={S.label} htmlFor="kb-video">Or a video link <span style={S.labelHint}>(YouTube or Vimeo; used when no video is uploaded)</span></label>
            <input id="kb-video" value={form.videoUrl} onChange={e => setForm({ ...form, videoUrl: e.target.value })}
              style={S.input} placeholder="https://vimeo.com/..." />
            <label style={S.label} htmlFor="kb-script">Video script <span style={S.labelHint}>(narration and the screens to show; Super Admins only)</span></label>
            <textarea id="kb-script" value={form.videoScript} onChange={e => setForm({ ...form, videoScript: e.target.value })} style={{ ...S.htmlBox, minHeight: 120, fontFamily: 'inherit' }} rows={6} />
            <label style={S.label} htmlFor="kb-prompt">Intro clip prompt <span style={S.labelHint}>(for an AI video tool; optional)</span></label>
            <textarea id="kb-prompt" value={form.videoPrompt} onChange={e => setForm({ ...form, videoPrompt: e.target.value })} style={{ ...S.htmlBox, minHeight: 70, fontFamily: 'inherit' }} rows={3} />
          </div>

          <div style={S.twoCol}>
            <div style={S.field}>
              <label style={S.label} htmlFor="kb-status">Status</label>
              <select id="kb-status" value={form.status} onChange={e => setForm({ ...form, status: e.target.value })} style={S.input}>
                <option value="draft">Draft (Super Admins only)</option>
                <option value="published">Published</option>
                <option value="archived">Archived</option>
              </select>
            </div>
            <div style={{ flex: '0 1 120px' }}>
              <label style={S.label} htmlFor="kb-order">Position</label>
              <input id="kb-order" type="number" value={form.order} onChange={e => setForm({ ...form, order: e.target.value })}
                style={S.input} />
            </div>
            <label style={{ ...S.field, display: 'flex', alignItems: 'center', gap: 8, marginTop: 30, fontSize: 14, color: 'var(--t-37474f)' }}>
              <input type="checkbox" checked={form.training} onChange={e => setForm({ ...form, training: e.target.checked })} />
              Part of the training
            </label>
          </div>

          <div style={S.formActions}>
            <button onClick={saveArticle} disabled={saving || !!upload} style={S.saveBtn}>
              {saving ? 'Saving…' : editing === 'new' ? 'Add article' : 'Save changes'}
            </button>
            <button onClick={() => { setEditing(null); setError(''); }} disabled={saving || !!upload} style={S.cancelBtn}>Cancel</button>
          </div>
        </div>
      )}

      {/* Article Detail View */}
      {viewing && (
        <div style={S.articleDetail}>
          {isAdmin && <div style={{ ...S.cardFoot, marginTop: 0, marginBottom: 10 }}>{tagsOf(openArticle)}</div>}
          <h2 style={S.detailTitle}>{openArticle.icon && <Emo e={openArticle.icon} className="cp-ico-once" style={{ marginRight: 10 }} />}{openArticle.title}</h2>

          {/* Video: ours first, else the YouTube / Vimeo link */}
          {openArticle.hasVideoFile ? (
            playUrl?.id === openArticle.id
              ? <video src={playUrl.url} controls preload="metadata" style={S.ownVideo} title={openArticle.title}
                  onTimeUpdate={e => watched(openArticle, e.currentTarget)} onEnded={e => watched(openArticle, e.currentTarget)} />
              : <div style={{ ...S.hint, marginBottom: 20 }}>Loading the video…</div>
          ) : openArticle.videoUrl && (
            <div style={S.videoWrap}>
              <iframe
                src={embedUrl(openArticle.videoUrl)}
                style={S.video}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                title={openArticle.title}
              />
            </div>
          )}

          <div className="kb-content" style={S.articleContent}
            dangerouslySetInnerHTML={{ __html: fluxHtml(openArticle.content) }} />

          {showTraining && openArticle.training && openArticle.status === 'published' && !viewAs && (
            <div style={S.doneRow}>
              <button onClick={() => toggleDone(openArticle)} style={done[openArticle.id] ? S.doneOn : S.doneBtn} aria-pressed={!!done[openArticle.id]}>
                {done[openArticle.id] ? '✓ Done' : 'Mark as done'}
              </button>
              <span style={S.hint}>{trainingDone} of {lists.training.length} in your training done{openArticle.hasVideoFile && !done[openArticle.id] ? ' · watching the whole video also marks it done' : ''}</span>
            </div>
          )}

          {openArticle.updatedAt && (
            <div style={S.articleMeta}>
              Last updated: {new Date(openArticle.updatedAt).toLocaleDateString('en-US', { timeZone: timeZone() })}
              {isAdmin && openArticle.updatedBy ? ` by ${openArticle.updatedBy}` : ''}
            </div>
          )}
        </div>
      )}

      {/* Views, search and list */}
      {!selected && !editing && (
        <>
          {views.length > 1 && (
            <div style={{ ...PILL_ROW, marginBottom: 14 }} role="tablist">
              {views.map(v => (
                <button key={v.id} role="tab" aria-selected={current === v.id} onClick={() => setView(v.id)} style={{ ...PILL, ...(current === v.id ? PILL_ON : {}) }}>{emoLabel(v.label)}</button>
              ))}
            </div>
          )}

          {current === 'training' && (
            <div style={S.trainingBox}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
                <strong style={{ color: 'var(--t-00695c)' }}>Your training: {trainingDone} of {lists.training.length} done</strong>
                <span style={S.hint}>The articles for your role, in order. Open one and click Mark as done.</span>
              </div>
              <div style={S.progressTrack}><div style={{ ...S.progressBar, width: `${lists.training.length ? (trainingDone / lists.training.length) * 100 : 0}%` }} /></div>
            </div>
          )}
          {current === 'novideo' && <p style={{ ...S.hint, marginBottom: 12 }}>Drafts and published articles with no video yet. Open one and use <Emo e="🎬" /> Video prompt to make its video.</p>}
          {current === 'archived' && <p style={{ ...S.hint, marginBottom: 12 }}>Only Super Admins see archived articles. Open one to bring it back.</p>}
          {current === 'drafts' && <p style={{ ...S.hint, marginBottom: 12 }}>Only Super Admins see drafts. Open one and click Publish when it’s ready.</p>}

          {lists[current].length === 0 ? (
            <div style={S.empty}>
              <div style={S.emptyIcon}><Emo e="📚" /></div>
              <div>{current === 'articles' ? `No articles yet.${isAdmin ? ' Click "Add Article" to create the first one.' : ''}` : 'Nothing here.'}</div>
            </div>
          ) : (
            <>
              <div style={S.searchWrap}>
                <span style={S.searchIcon} aria-hidden="true"><Emo e="🔍" /></span>
                <input type="search" value={query} onChange={e => setQuery(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Escape') setQuery(''); }}
                  placeholder="Search the Library" aria-label="Search the Library" style={S.search} />
              </div>
              <div style={S.countRow} aria-live="polite">
                <span>
                  {!words.length ? `${lists[current].length} ${lists[current].length === 1 ? 'article' : 'articles'}`
                    : shown.length ? `${shown.length} ${shown.length === 1 ? 'article matches' : 'articles match'} "${query.trim()}"`
                    : `No articles match "${query.trim()}"`}
                </span>
                {query && <button onClick={() => setQuery('')} style={S.linkBtn}>Clear</button>}
              </div>

              {shown.length === 0 ? (
                <div style={S.empty}>
                  <div style={S.emptyIcon}><Emo e="🔍" /></div>
                  <div>Try fewer or different words.</div>
                </div>
              ) : grouped.map(g => (
                <section key={g.id} style={{ marginBottom: 18 }}>
                  <h3 style={S.sectionTitle}><Emo e={g.icon} /> {g.label}</h3>
                  <div style={S.cardGrid}>
                    {g.items.map(({ article, text }) => {
                      // Colours follow the article's place in the full list, so they don't jump while searching
                      const i = articles.indexOf(article);
                      return (
                        <div key={article.id} role="button" tabIndex={0} onClick={() => setSelected(article.id)}
                          onKeyDown={e => { if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); setSelected(article.id); } }}
                          style={S.card} className="cp-ico-host">
                          <div style={{ ...S.cardIcon, background: COLORS[i % 5], color: ICON_COLORS[i % 5], position: 'relative' }}>
                            <Emo e={article.icon || g.icon} />
                            {/* My training: done or not yet, on the icon (3.36.3) */}
                            {current === 'training' && (
                              <span style={{ ...S.doneBadge, ...(done[article.id] ? S.doneBadgeOn : {}) }} aria-label={done[article.id] ? 'Done' : 'Not done yet'}>{done[article.id] ? '✓' : '○'}</span>
                            )}
                          </div>
                          <div style={S.cardBody}>
                            <h3 style={S.cardTitle}><Highlight text={article.title || ''} re={matchRe} /></h3>
                            <p style={S.cardSummary}><Highlight text={summary(text, words)} re={matchRe} /></p>
                            {(hasVideo(article) || isAdmin) && (
                              <div style={S.cardFoot}>
                                {hasVideo(article) && <span style={S.videoTag}>▶ Video included</span>}
                                {tagsOf(article)}
                                {isAdmin && (
                                  <>
                                    <button onClick={(e) => startEdit(article, e)} style={S.cardEditBtn}><Emo e="✏️" /> Edit</button>
                                    <button onClick={(e) => { e.stopPropagation(); setPrompt(article); }} style={S.cardEditBtn}><Emo e="🎬" /> Prompt</button>
                                  </>
                                )}
                              </div>
                            )}
                          </div>
                          <div style={S.cardArrow}>→</div>
                        </div>
                      );
                    })}
                  </div>
                </section>
              ))}
            </>
          )}
        </>
      )}

      {/* Video prompt: the script and the intro clip prompt, to copy into a video tool */}
      {prompt && (
        <div style={S.overlay} onMouseDown={e => { if (e.target === e.currentTarget) setPrompt(null); }}>
          <div style={S.modal} role="dialog" aria-modal="true" aria-label="Video prompt">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
              <strong style={{ color: 'var(--t-00695c)', fontSize: 16 }}><Emo e="🎬" /> Video prompt: {prompt.title}</strong>
              <button onClick={() => setPrompt(null)} style={S.closeX} aria-label="Close">✕</button>
            </div>
            {!prompt.videoScript && !prompt.videoPrompt ? (
              <p style={S.hint}>No prompt yet. Add a video script in Edit.</p>
            ) : (
              [['Video script', prompt.videoScript, 'Narration and the screens to show. Paste it into your video tool (Google Vids: Create → paste), or read it as the voice-over.'],
               ['Intro clip prompt', prompt.videoPrompt, 'For an AI video tool (Gemini Veo and similar): a short opening shot. AI tools can’t show the real portal screens.']]
                .filter(([, v]) => v).map(([label, v, help]) => (
                  <div key={label} style={{ marginTop: 14 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                      <span style={{ ...S.label, margin: 0 }}>{label}</span>
                      <button style={S.cardEditBtn} onClick={() => navigator.clipboard.writeText(v).then(() => notify({ tone: 'success', title: 'Copied' }), () => notify({ tone: 'error', title: "Couldn't copy" }))}><Emo e="📋" /> Copy</button>
                    </div>
                    <p style={{ ...S.hint, marginTop: 2 }}>{help}</p>
                    <pre style={S.promptBox}>{v}</pre>
                  </div>
                ))
            )}
            <p style={{ ...S.hint, marginTop: 14 }}>When the video is ready: Edit → Upload a video (or paste a YouTube / Vimeo link). Use sample data only, never real faxes.</p>
          </div>
        </div>
      )}
    </div>
  );
}

// How articles look when read (the editor uses EDITOR_CSS)
const KB_CSS = `
.kb-content{overflow-wrap:anywhere}
.kb-content h3{margin:22px 0 10px;font-size:19px;color:var(--t-00695c)}
.kb-content h4{margin:18px 0 8px;font-size:16px;color:var(--t-263238)}
.kb-content p{margin:0 0 12px}
.kb-content ul,.kb-content ol{margin:0 0 12px;padding-left:24px}
.kb-content a{color:var(--t-00897b)}
.kb-content img{max-width:100%;height:auto}
.kb-content iframe,.kb-content video{max-width:100%}
.kb-content table{max-width:100%;display:block;overflow-x:auto}
`;

const S = {
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap', marginBottom: 20 },
  pageTitle: { margin: '0 0 4px', color: 'var(--t-00695c)', fontSize: 22, fontWeight: 700 },
  pageSubtitle: { margin: 0, color: 'var(--t-78909c)', fontSize: 14 },
  backBtn: {
    background: 'none', border: '1.5px solid var(--c-e0e0e0)', borderRadius: 8,
    padding: '8px 16px', cursor: 'pointer', fontSize: 14, color: 'var(--t-00695c)',
    fontWeight: 600, fontFamily: 'inherit'
  },
  addBtn: {
    padding: '9px 20px', background: 'var(--c-00897b)', color: 'var(--t-ffffff)', border: 'none',
    borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 600, whiteSpace: 'nowrap', fontFamily: 'inherit'
  },
  editBtn: {
    padding: '8px 14px', background: 'var(--c-e3f2fd)', color: 'var(--t-1565c0)', border: 'none',
    borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 500, fontFamily: 'inherit'
  },
  deleteBtn: {
    padding: '8px 14px', background: 'var(--c-fce4ec)', color: 'var(--t-b71c1c)', border: 'none',
    borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 500, fontFamily: 'inherit'
  },
  errorBanner: {
    background: 'var(--c-fce4ec)', color: 'var(--t-b71c1c)', padding: '12px 18px',
    borderRadius: 8, marginBottom: 16, fontSize: 14
  },

  // Search
  searchWrap: { position: 'relative', marginBottom: 8 },
  searchIcon: { position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', fontSize: 14, opacity: 0.6, pointerEvents: 'none' },
  search: {
    width: '100%', boxSizing: 'border-box', padding: '11px 14px 11px 38px', borderRadius: 10,
    border: '1.5px solid var(--c-e0e0e0)', fontSize: 15, color: 'var(--t-333333)', fontFamily: 'inherit', background: 'var(--c-ffffff)'
  },
  countRow: { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', fontSize: 13, color: 'var(--t-78909c)', margin: '0 2px 14px', overflowWrap: 'anywhere' },
  mark: { background: 'var(--c-fff59d)', color: 'inherit', borderRadius: 3, padding: '0 1px' },

  // Cards
  cardGrid: { display: 'flex', flexDirection: 'column', gap: 14 },
  card: {
    background: 'var(--c-ffffff)', borderRadius: 12, padding: 'clamp(14px, 3vw, 20px) clamp(14px, 3.5vw, 24px)',
    boxShadow: '0 1px 6px rgb(var(--c-000000-rgb) / 0.07)', border: '1px solid var(--b-e8f5e9)',
    display: 'flex', alignItems: 'center', gap: 'clamp(10px, 3vw, 18px)', cursor: 'pointer',
    transition: 'box-shadow 0.2s, border-color 0.2s'
  },
  cardIcon: {
    width: 48, height: 48, borderRadius: 12, display: 'flex',
    alignItems: 'center', justifyContent: 'center', fontSize: 22, flexShrink: 0
  },
  cardBody: { flex: 1, minWidth: 0 },
  cardTitle: { margin: '0 0 6px', color: 'var(--t-00695c)', fontSize: 16, fontWeight: 700, overflowWrap: 'anywhere' },
  cardSummary: { margin: 0, color: 'var(--t-546e7a)', fontSize: 13, lineHeight: 1.5, overflowWrap: 'anywhere' },
  cardFoot: { display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', marginTop: 8 },
  videoTag: {
    display: 'inline-block', background: 'var(--c-e8f5e9)', color: 'var(--t-2e7d32)',
    fontSize: 12, fontWeight: 600, padding: '2px 10px', borderRadius: 20
  },
  cardArrow: { color: 'var(--t-b0bec5)', fontSize: 20, flexShrink: 0 },
  cardEditBtn: {
    background: 'var(--c-e3f2fd)', color: 'var(--t-1565c0)', border: 'none', borderRadius: 6,
    padding: '3px 9px', cursor: 'pointer', fontSize: 12, fontWeight: 600, fontFamily: 'inherit'
  },
  cardDeleteBtn: {
    background: 'var(--c-fce4ec)', color: 'var(--t-b71c1c)', border: 'none', borderRadius: 6,
    padding: '3px 9px', cursor: 'pointer', fontSize: 12, fontWeight: 600, fontFamily: 'inherit'
  },

  // Reading an article
  articleDetail: {
    background: 'var(--c-ffffff)', borderRadius: 12, padding: 'clamp(16px, 4vw, 32px)',
    boxShadow: '0 1px 6px rgb(var(--c-000000-rgb) / 0.07)', border: '1px solid var(--b-e8f5e9)'
  },
  detailTitle: { margin: '0 0 24px', color: 'var(--t-00695c)', fontSize: 22, fontWeight: 700, overflowWrap: 'anywhere' },
  videoWrap: { position: 'relative', paddingBottom: '56.25%', height: 0, marginBottom: 24, borderRadius: 8, overflow: 'hidden' },
  video: { position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 'none' },
  articleContent: { color: 'var(--t-444444)', fontSize: 15, lineHeight: 1.7 },
  articleMeta: { marginTop: 24, fontSize: 12, color: 'var(--t-b0bec5)', borderTop: '1px solid var(--c-f0f0f0)', paddingTop: 12 },

  // Editing
  formCard: {
    background: 'var(--c-ffffff)', borderRadius: 12, padding: 'clamp(14px, 3.5vw, 24px)', marginBottom: 24,
    boxShadow: '0 1px 6px rgb(var(--c-000000-rgb) / 0.07)', border: '1px solid var(--b-e8f5e9)'
  },
  formTitle: { margin: '0 0 6px', color: 'var(--t-00695c)', fontSize: 17 },
  label: { display: 'block', fontSize: 11, fontWeight: 700, color: 'var(--t-78909c)', textTransform: 'uppercase', letterSpacing: '0.4px', margin: '14px 0 5px' },
  labelHint: { textTransform: 'none', letterSpacing: 0, fontWeight: 400 },
  labelRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, margin: '14px 0 5px' },
  input: {
    width: '100%', padding: '8px 10px', borderRadius: 8, border: '1.5px solid var(--c-e0e0e0)',
    fontSize: 14, color: 'var(--t-333333)', fontFamily: 'inherit', boxSizing: 'border-box'
  },
  htmlBox: {
    width: '100%', minHeight: 260, padding: '10px 12px', borderRadius: 8, border: '1.5px solid var(--c-e0e0e0)',
    fontSize: 13, lineHeight: 1.5, color: 'var(--t-263238)', fontFamily: 'ui-monospace, Menlo, monospace', resize: 'vertical', boxSizing: 'border-box', display: 'block'
  },
  hint: { margin: '6px 0 0', color: 'var(--t-90a4ae)', fontSize: 12, lineHeight: 1.45 },
  twoCol: { display: 'flex', gap: 12, flexWrap: 'wrap' },
  linkBtn: { padding: '2px 4px', border: 'none', background: 'none', color: 'var(--t-00897b)', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', textDecoration: 'underline' },
  formActions: { display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 20 },
  saveBtn: {
    padding: '9px 18px', background: 'var(--c-00897b)', color: 'var(--t-ffffff)', border: 'none',
    borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 700, fontFamily: 'inherit'
  },
  cancelBtn: {
    padding: '8px 14px', background: 'var(--c-ffffff)', border: '1.5px solid var(--c-cfd8dc)',
    borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600, color: 'var(--t-455a64)', fontFamily: 'inherit'
  },

  // 3.35.0
  tag: { display: 'inline-block', background: 'var(--c-eceff1)', color: 'var(--t-455a64)', fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 20 },
  publishBtn: { padding: '8px 14px', background: 'var(--c-00897b)', color: 'var(--t-ffffff)', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 700, fontFamily: 'inherit' },
  field: { flex: '1 1 180px', minWidth: 0 },
  sectionTitle: { margin: '4px 2px 10px', color: 'var(--t-00695c)', fontSize: 15, fontWeight: 700 },
  videoBox: { marginTop: 16, padding: '12px 14px', border: '1px solid var(--c-e0f2f1)', borderRadius: 10, background: 'var(--c-f7faf9)' },
  fileRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, fontSize: 13, color: 'var(--t-37474f)', marginBottom: 8, flexWrap: 'wrap' },
  uploadBtn: { display: 'inline-block', padding: '7px 14px', background: 'var(--c-ffffff)', border: '1.5px solid var(--c-80cbc4)', color: 'var(--t-00695c)', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 700 },
  progressTrack: { height: 8, background: 'var(--c-e0f2f1)', borderRadius: 4, overflow: 'hidden', marginTop: 8 },
  progressBar: { height: '100%', background: 'var(--c-00897b)', borderRadius: 4, transition: 'width .3s' },
  ownVideo: { width: '100%', maxHeight: '70vh', borderRadius: 8, background: 'var(--c-000000)', marginBottom: 24, display: 'block' },
  doneRow: { display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginTop: 24, paddingTop: 16, borderTop: '1px solid var(--c-f0f0f0)' },
  doneBtn: { padding: '9px 18px', background: 'var(--c-ffffff)', border: '1.5px solid var(--c-00897b)', color: 'var(--t-00695c)', borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 700, fontFamily: 'inherit' },
  doneBadge: { position: 'absolute', right: -6, bottom: -6, width: 20, height: 20, borderRadius: '50%', background: 'var(--c-ffffff)', border: '1.5px solid var(--c-cfd8dc)', color: 'var(--t-b0bec5)', fontSize: 12, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1 },
  doneBadgeOn: { background: 'var(--c-2e7d32)', borderColor: 'var(--c-2e7d32)', color: 'var(--t-ffffff)' },
  doneOn: { padding: '9px 18px', background: 'var(--c-e8f5e9)', border: '1.5px solid var(--c-66bb6a)', color: 'var(--t-2e7d32)', borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 700, fontFamily: 'inherit' },
  trainingBox: { background: 'var(--c-ffffff)', border: '1px solid var(--c-e0f2f1)', borderRadius: 12, padding: '12px 16px', marginBottom: 14 },
  overlay: { position: 'fixed', inset: 0, background: 'rgb(var(--c-0f2823-rgb) / 0.45)', zIndex: 1200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 },
  modal: { background: 'var(--c-ffffff)', borderRadius: 14, padding: 20, width: 'min(760px, 100%)', maxHeight: '86vh', overflowY: 'auto', boxSizing: 'border-box' },
  closeX: { border: 'none', background: 'var(--c-eceff1)', color: 'var(--t-546e7a)', borderRadius: '50%', width: 28, height: 28, cursor: 'pointer', fontWeight: 800, fontFamily: 'inherit' },
  promptBox: { whiteSpace: 'pre-wrap', background: 'var(--c-f7faf9)', border: '1px solid var(--c-e0f2f1)', borderRadius: 8, padding: '10px 12px', fontSize: 13, lineHeight: 1.5, color: 'var(--t-263238)', fontFamily: 'inherit', margin: '6px 0 0', maxHeight: 320, overflowY: 'auto' },

  empty: { textAlign: 'center', padding: '48px 0', color: 'var(--t-90a4ae)', fontSize: 15 },
  emptyIcon: { fontSize: 40, marginBottom: 12 }
};
